import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { HardHatIcon, PackageIcon, TruckIcon, FactoryIcon, StoreIcon, HeartIcon, ArrowRightIcon } from 'lucide-react';
import { applications } from '../data/applications';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useTranslation } from '../lib/i18n';
interface ApplicationsPageProps {
  onRequestQuote: () => void;
}
export function ApplicationsPage({
  onRequestQuote
}: ApplicationsPageProps) {
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  const ru = lang === 'ru';
  const iconMap: Record<string, ComponentType<{
    className?: string;
  }>> = {
    HardHat: HardHatIcon,
    Package: PackageIcon,
    Truck: TruckIcon,
    Factory: FactoryIcon,
    Store: StoreIcon,
    Heart: HeartIcon
  };
  return <main className="min-h-screen bg-dark-bg" data-id="element-680" data-mp="1o2e40yqo2jem">
      {/* Hero */}
      <section className="py-20 bg-dark-surface border-b-2 border-dark-border" data-id="element-681" data-mp="1k2ncutx8a19k">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-682" data-mp="33phrwan9blcw">
          <h1 className="text-5xl md:text-6xl font-black text-white mb-6" data-id="element-683" data-mp="38p45il6zz3m8">
            <span className="text-brand-yellow" data-id="element-684" data-mp="3dgcghlnz6x1i">
              {en ? 'Applications' : ru ? 'Применение' : 'Primene'}
            </span>{' '}
            {en ? 'across industries' : ru ? 'в различных отраслях' : 'u različitim industrijama'}
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto" data-id="element-685" data-mp="2pqi9vlu924pp">
            {en ? 'D-TECH equipment solves real challenges across professional environments. Discover how our solutions can improve your operations.' : ru ? 'Оборудование D-TECH решает реальные задачи в профессиональной среде. Узнайте, как наши решения могут улучшить ваши операции.' : 'D-TECH oprema rešava realne izazove u različitim profesionalnim okruženjima. Otkrijte kako naša rešenja mogu unaprediti vaše operacije.'}
          </p>
        </div>
      </section>

      {/* Applications Grid */}
      <section className="py-20" data-id="element-686" data-mp="261pc1bttw982">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-687" data-mp="264v0coumv72q">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-688" data-mp="2k1i5hewqp3jq">
            {applications.map(app => {
            const Icon = iconMap[app.icon] || PackageIcon;
            const title = en && app.titleEn ? app.titleEn : ru && app.titleRu ? app.titleRu : app.title;
            const problem = en && app.problemEn ? app.problemEn : ru && app.problemRu ? app.problemRu : app.problem;
            const solution = en && app.solutionEn ? app.solutionEn : ru && app.solutionRu ? app.solutionRu : app.solution;
            return <Card key={app.id} hover data-id="element-689" data-mp="ugjk7gbc20tm">
                  <div className="flex items-start space-x-4 mb-6" data-id="element-690" data-mp="z8b2z4o8z297">
                    <div className="w-14 h-14 bg-brand-yellow rounded-industrial flex items-center justify-center flex-shrink-0" data-id="element-691" data-mp="zannnple3qpy">
                      <Icon className="w-7 h-7 text-dark-bg" data-id="element-692" data-mp="pi88t15e3i9a" />
                    </div>
                    <div className="flex-1" data-id="element-693" data-mp="1d8lri12344r7">
                      <h3 className="text-2xl font-bold text-white mb-2" data-id="element-694" data-mp="1d9nen3wdkhpa">
                        {title}
                      </h3>
                    </div>
                  </div>
                  <div className="space-y-4" data-id="element-695" data-mp="13axypgo1h7bo">
                    <div data-id="element-696" data-mp="13c8yz6i9zwy1">
                      <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-697" data-mp="188zg7x5tgyl1">
                        {en ? 'Problem' : ru ? 'Проблема' : 'Problem'}
                      </h4>
                      <p className="text-gray-300 leading-relaxed" data-id="element-698" data-mp="3sz8czqw678rr">{problem}</p>
                    </div>
                    <div data-id="element-699" data-mp="d1qol0as3xzc">
                      <h4 className="text-sm font-bold text-brand-yellow uppercase tracking-wider mb-2" data-id="element-700" data-mp="d2a4kbgvccaq">
                        {en ? 'Solution' : ru ? 'Решение' : 'Rešenje'}
                      </h4>
                      <p className="text-white leading-relaxed" data-id="element-701" data-mp="3c74xsf8s03f">{solution}</p>
                    </div>
                  </div>
                </Card>;
          })}
          </div>

          {/* CTA Section */}
          <div className="bg-dark-surface border-2 border-dark-border rounded-industrial p-12 text-center" data-id="element-702" data-mp="vzbco55pysiw">
            <h2 className="text-3xl font-black text-white mb-4" data-id="element-703" data-mp="qyx2ohkk58i8">
              {en ? "Don't see your industry?" : ru ? 'Не видите свою отрасль?' : 'Ne vidite vašu industriju?'}
            </h2>
            <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto" data-id="element-704" data-mp="36t79wkdujumy">
              {en ? 'Our equipment adapts to a wide range of professional environments. Contact us to discuss your specific use case.' : ru ? 'Наше оборудование адаптируется к широкому спектру профессиональных сред. Свяжитесь с нами, чтобы обсудить ваш конкретный случай использования.' : 'Naša oprema se prilagođava različitim profesionalnim okruženjima. Kontaktirajte nas da razgovaramo o vašoj specifičnoj primeni.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-705" data-mp="20zj3qmuiak9b">
              <Button variant="primary" size="lg" onClick={onRequestQuote} data-id="element-706" data-mp="250l3d4sc4ybt">
                {en ? 'Request a quote' : ru ? 'Запросить цену' : 'Zatražite ponudu'}
              </Button>
              <Link to="/contact" data-id="element-707" data-mp="2ey92s80vyu0b">
                <Button variant="secondary" size="lg" data-id="element-708" data-mp="fln2jkeclha7">
                  {en ? 'Contact us' : ru ? 'Свяжитесь с нами' : 'Kontaktirajte nas'}
                  <ArrowRightIcon className="w-5 h-5 ml-2 inline" data-id="element-709" data-mp="cja6hkkzg7sm" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>;
}